/**
 * React integration for Silospot i18n.
 * Exposes a provider and re-exports `useTranslation` from react-i18next.
 */

import { DEFAULT_LOCALE, type Locale } from '@silospot/shared';
import i18next from 'i18next';
import { type ReactElement, type ReactNode, useEffect } from 'react';
import { I18nextProvider, initReactI18next, useTranslation } from 'react-i18next';
import { defaultNS, namespaces, resources } from './resources';

let initialized = false;

export function initI18n(locale: Locale = DEFAULT_LOCALE): typeof i18next {
  if (!initialized) {
    void i18next.use(initReactI18next).init({
      resources,
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      defaultNS,
      ns: namespaces,
      interpolation: { escapeValue: false },
    });
    initialized = true;
  }
  return i18next;
}

export interface I18nProviderProps {
  children: ReactNode;
  locale?: Locale;
}

export function I18nProvider({
  children,
  locale = DEFAULT_LOCALE,
}: I18nProviderProps): ReactElement {
  const instance = initI18n(locale);

  useEffect(() => {
    if (instance.language !== locale) {
      void instance.changeLanguage(locale);
    }
  }, [instance, locale]);

  return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
}

export { useTranslation };
